import { reactive, UnwrapRef, Ref, ref, ComponentInternalInstance, getCurrentInstance } from '@vue/composition-api';
import dayjs from 'dayjs';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import { httpUrl } from '@/renderer/scripts/http';
import { copyText2Clipboard } from '@/renderer/scripts/utils/tools/index';
import store from '@/renderer/store';
import { DomainItem } from '@/renderer/scripts/libs/smh-sdk-class/typings';

export enum GenerateCodeStatus {
  NOT_GENERATED = 'NOT_GENERATED',
  IS_STOP = 'IS_STOP',
  IS_START = 'IS_START',
}
interface SpaceConfig {
  allowPersonalSpace: boolean;
  personalSpaceQuotaSize: string;
}
interface SpaceSizeInfo extends SpaceConfig {
  orgAvailableSpace: string;
  orgAvailableSpaceInfo: {
    size: number;
    unit: string;
  };
}
type Handler = {
  loadingUrlInfo: Ref<boolean>;
  inviteUrlInfo: UnwrapRef<{
    codeStatus: GenerateCodeStatus;
    expireTime: string;
    expired: boolean;
    enabled: boolean;
    code: string;
    domains: DomainItem[];
    invitedCount: number;
    userLimitReached: boolean;
  }>;
  // spaceSizeInfo: SpaceSizeInfo;
  methods: {
    initData: () => void;
    initInternalData: () => void;
    checkIfHasInviteUrl: () => Promise<void>;
    getInviteCodeInfo: (code: string) => Promise<void>;
    generateInviteUrl: () => Promise<void>;
    updateInviteCodeInfo: (type: 'stop' | 'recovery' | string | undefined, _params?: Object) => Promise<void>;
  };
};
export default function handler(): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  // 当前组件
  const inviteUrlInfo = reactive({
    codeStatus: GenerateCodeStatus.NOT_GENERATED,
    expireTime: '',
    code: '',
    domains: [
      {
        domain: '',
        isCustom: false,
        status: 0,
      },
    ],
    enabled: true,
    expired: false,
    invitedCount: 0,
    userLimitReached: false,
    groupName: '',
  });
  const loadingUrlInfo = ref(false);
  const { defaultUserOptions } = vm.$store.getters;
  const methods = {
    async initData() {
      loadingUrlInfo.value = true;
      await methods.checkIfHasInviteUrl();
      loadingUrlInfo.value = false;
    },
    // 初始化当前已邀请的企业成员
    initInternalData() {
      methods.checkIfHasInviteUrl();
    },
    async generateInviteUrl() {
      try {
        const params = {
          role: 'user',
        };
        const {
          data: { code },
        } = await smh.userAPI.generateExtInviteCode();
        await methods.getInviteCodeInfo(code);
        // 创建链接时拼接url，避免第一次取不到正确的url
        const copyContent = `${httpUrl.webUrl}/web/invitation/${encodeURIComponent(code)}`;
        copyText2Clipboard(copyContent)
          .then(() => {
            tips.success(i18n_t('邀请链接已复制到剪切板'));
          })
          .catch(() => {
            tips.error(i18n_t('邀请链接复制失败'));
          });
        /* reporter.report('background_user_manage', {
          add_type: 'business',
          add_result: 1,
        });*/
      } catch (error) {
        tips.error(i18n_t('生成邀请链接失败'));
        /* reporter.report('background_user_manage', {
          add_type: 'business',
          add_result: 0,
        });*/
      }
    },

    async checkIfHasInviteUrl() {
      try {
        // 1. 查询邀请码
        const {
          data: { code },
        } = await smh.userAPI.getExtInviteCode();
        // 2. 查询邀请码是否已停止
        methods.getInviteCodeInfo(code);
      } catch (error) {
        inviteUrlInfo.codeStatus = GenerateCodeStatus.NOT_GENERATED;
      }
    },
    async getInviteCodeInfo(code: string) {
      try {
        const { data: codeInfo } = await smh.userAPI.getInviteCodeInfo({
          code,
        });
        // 拿到当前企业的配置信息，这个是可加入的用户数量
        const { userCountLimit } = store.getters.editionConfig;
        if (codeInfo.enabled && !codeInfo.expired) {
          inviteUrlInfo.codeStatus = GenerateCodeStatus.IS_START;
        } else {
          inviteUrlInfo.codeStatus = GenerateCodeStatus.IS_STOP;
        }
        inviteUrlInfo.enabled = codeInfo.enabled;
        inviteUrlInfo.expired = codeInfo.expired;
        inviteUrlInfo.code = codeInfo.code;
        inviteUrlInfo.domains = codeInfo.domains;
        inviteUrlInfo.expireTime = codeInfo.expireTime;
        inviteUrlInfo.invitedCount = codeInfo.invitedCount;
        inviteUrlInfo.userLimitReached = codeInfo.currentOrgUserCount >= userCountLimit;
        inviteUrlInfo.groupName = codeInfo.organization.name;
      } catch (error) {
        inviteUrlInfo.codeStatus = GenerateCodeStatus.NOT_GENERATED;
      }
    },
    async updateInviteCodeInfo(type: 'stop' | 'recovery' | string | undefined, _params?: Object) {
      const params = {
        code: inviteUrlInfo.code,
      };
      if (type === 'stop') {
        Object.assign(params, {
          expireTime: dayjs(Date.now()).format('YYYY-MM-DD HH:mm'),
          enabled: false,
        });
      } else if (type === 'recovery') {
        Object.assign(params, {
          expireTime: dayjs(Date.now()).add(1, 'days').format('YYYY-MM-DD HH:mm'),
          enabled: true,
        });
      } else {
        Object.assign(params, _params);
      }
      try {
        await smh.userAPI.updateInviteCode(params);
        methods.getInviteCodeInfo(inviteUrlInfo.code);
        if (type === 'recovery') {
          reporter.report('background_user_manage', {
            add_type: 'business',
            add_result: 1,
          });
        }
        tips.success(i18n_t('设置成功'));
      } catch (error) {
        tips.error(error as MsgError);
        if (type === 'recovery') {
          reporter.report('background_user_manage', {
            add_type: 'business',
            add_result: 0,
          });
        }
      }
    },
    // 重新生成邀请码code：需先删除旧code再重新生成
    async regenerateInviteUrl() {
      // 删除原inviteUrlInfo.code
      try {
        await smh.userAPI.deleteInviteCode({
          code: inviteUrlInfo.code,
        });
        methods.generateInviteUrl();
      } catch (err: MsgError) {
        tips.error(err);
      }
    },
  };
  return {
    loadingUrlInfo,
    inviteUrlInfo,
    methods,
  };
}
